// 🔩 Base
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

// 🇫🇷 i18n
import { injectIntl } from 'react-intl';
import { LocalizedMessage } from 'intl/LocalizedComponents';

// 🧱 Components
import Popover from 'components/core/Popover';
import { Calendar } from '@material-ui/pickers';
import Tooltip from 'components/core/Tooltip';
import Button2 from 'components/buttons/Button2/Button2';

// 🔧 Utils
import moment from 'moment';
import useResponsive from 'hooks/useResponsive';
import { localeData } from 'utils/DateUtils';

// 🖼️ Assets
import { FLOW_COLOR } from 'constants/App';

// Icons
import InformationIcon from 'assets/icons/information';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh.svg';

import commonStyles from '../inputs/common.cssmodule.css';

// Date utils
// import DateTimeItem from './DateTimeItem';

const getTimeRange = (date) => {
  if (!date) return null;
  return date.getHours() >= 12 ? 'pm' : 'am';
};

const isSameDay = (date1, date2) => (
  date1.getFullYear() === date2.getFullYear()
    && date1.getMonth() === date2.getMonth()
    && date1.getDate() === date2.getDate()
);

const formatDate = (date, intl, formatOption) => (date instanceof Date ? new Intl.DateTimeFormat(intl.locale || intl.defaultLocale.locale, formatOption).format(date) : '');

// 🧰 Dates configuration
const dateFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const timeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

const POPOVER_HORIZONTAL_OFFSET = '-30';

const selectTimeStyles = {
  border: '1px solid var(--color-sky-gray3, #E3EBF6)',
  borderRadius: '6px',
  padding: ' 8px 12px',
};

const errorTextStyles = {
  color: 'rgba(188, 56, 56, 1)',
  alignSelf: 'flex-start',
  textAlign: 'left',
  fontSize: '0.75rem',
  lineHeight: 1.3,
  marginTop: 5,
  letterSpacing: '0.03333em',
};

const amPmStyles = {
  fontSize: '18px',
  zIndex: 1,
  transition: 'color 0.3s',
};

const overlayStyles = {
  position: 'absolute',
  zIndex: 0,
  width: '100%',
  height: '50%',
  left: '50%',
  transition: 'transform 0.3s',
  background: FLOW_COLOR,
  borderRadius: '5px',
};

const verticalDividerStyle = {
  width: '1px',
  height: '22px',
  background: 'var(--color-sky-gray3)',
};

const getTextStyle = (hasValue) => ({
  color: hasValue ? 'var(--color-night-gray-9)' : 'var(--color-blue-gray-normal)',
  fontFamily: 'Roboto, Arial, sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '22px',
  letterSpacing: '0.14px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const iconStyle = {
  stroke: 'var(--color-night-gray9)',
  fontSize: '24px',
  marginRight: '8px',
  flexShrink: 0,
};

const inputContainerDisabled = {
  cursor: 'not-allowed',
  backgroundColor: 'var(--color-sky-gray2)',
  border: 'var(--border)',
};

const crossIconStyle = {
  stroke: 'var(--color-night-gray9)',
  fontSize: '20px',
  cursor: 'pointer',
  marginLeft: '8px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

/* ------------------ */
/*     Component      */
/* ------------------ */
const DateTimePicker3 = ({
  // name,
  type,
  disabled,

  tooltipId,
  required,
  date,
  minDate,
  maxDate,
  onChange,
  popoverProps,

  intl,
  placeholderId,
  label,
  calendarLabelId,
  dateOnly,
  errorText,
  hint,
}) => {
  const buttonRef = React.useRef(null);
  const isMobile = useResponsive();

  // Elements (new)
  const InformationTooltip = tooltipId && (
    <Tooltip content={(<LocalizedMessage id={tooltipId} />)}>
      <InformationIcon className={commonStyles.infoIcon} />
    </Tooltip>
  );

  // 🔄 State
  const [openCalendar, setOpenCalendar] = React.useState(false);
  const [temporarySelectedDate, setTemporarySelectedDate] = React.useState(date || minDate || new Date());

  const usesAmPm = localeData.usesAmPm();
  const timeRange = getTimeRange(temporarySelectedDate);

  // On items

  const hasMinDate = temporarySelectedDate && minDate;
  const hasMaxDate = temporarySelectedDate && maxDate;

  /* HOURS */
  /* ===== */
  const hoursRange = Array.from({ length: 24 }, (_, i) => i);
  const isSameMinDay = hasMinDate && isSameDay(minDate, temporarySelectedDate);
  const isSameMaxDay = hasMaxDate && isSameDay(maxDate, temporarySelectedDate);

  let OptionsHours = hoursRange.map((selectableHour) => {
    const hourBeforeMinDateHour = isSameMinDay && selectableHour < minDate.getHours();
    const hourAfterMaxDateHour = isSameMaxDay && selectableHour > maxDate.getHours();
    const displayedHour = usesAmPm ? selectableHour % 12 || 12 : selectableHour;

    return (
      <option
        key={selectableHour}
        disabled={hourBeforeMinDateHour || hourAfterMaxDateHour}
        value={selectableHour}
      >
        {displayedHour}
      </option>
    );
  });

  if (usesAmPm) OptionsHours = OptionsHours.slice(timeRange === 'am' ? 0 : 12, timeRange === 'am' ? 12 : 24);

  /* MINUTES */
  /* ======= */
  const minutesRange = Array.from({ length: 60 }, (_, i) => i);
  const isSameMinHour = hasMinDate && minDate.getHours() === temporarySelectedDate.getHours();
  const isSameMaxHour = hasMaxDate && maxDate.getHours() === temporarySelectedDate.getHours();

  const OptionsMinutes = minutesRange.map((selectableMinute) => {
    const minuteBeforeMinDateMinute = isSameMinDay && isSameMinHour && minDate && selectableMinute <= minDate.getMinutes();
    const minuteAfterMaxDateMinute = isSameMaxDay && isSameMaxHour && maxDate && selectableMinute >= maxDate.getMinutes();

    const displayedMinute = String(selectableMinute).padStart(2, '0');

    return <option key={selectableMinute} disabled={(minuteBeforeMinDateMinute) || (minuteAfterMaxDateMinute)} value={selectableMinute}>{displayedMinute}</option>;
  });

  /* HANDLERS */
  /* ======== */

  const handleDateChange = (momentDate) => {
    let newTemporarySelectedDate = momentDate.toDate();

    // If we try to select min date, we must be sure date and hour already selected are not before date and hour of min date
    if (minDate && newTemporarySelectedDate < minDate) {
      newTemporarySelectedDate = new Date(minDate);
      if (type === 'end') newTemporarySelectedDate.setMinutes(newTemporarySelectedDate.getMinutes() + 1);
    }

    // If we try to select max date, we must be sure date and hour already selected are not after date and hour of max date
    if (maxDate && newTemporarySelectedDate > maxDate) {
      newTemporarySelectedDate = new Date(maxDate);
      if (type === 'begin') newTemporarySelectedDate.setMinutes(newTemporarySelectedDate.getMinutes() - 1);
    }

    setTemporarySelectedDate(newTemporarySelectedDate);
  };

  const handleSelectHour = (e) => {
    const newTemporarySelectedDate = new Date(temporarySelectedDate);
    const newSelectedHour = e.target.value;
    newTemporarySelectedDate.setHours(newSelectedHour);
    setTemporarySelectedDate(newTemporarySelectedDate);
  };

  const handleSelectMinute = (e) => {
    const newSelectedDate = new Date(temporarySelectedDate);
    const newSelectedMinute = e.target.value;
    newSelectedDate.setMinutes(newSelectedMinute);
    setTemporarySelectedDate(newSelectedDate);
  };

  const handleSelectTimeRange = (e) => {
    const selectedTimeRange = e.target.value;
    setTemporarySelectedDate((prevDate) => {
      if (timeRange !== selectedTimeRange) {
        if (selectedTimeRange === 'am') {
          const newTemporarySelectedDate = new Date(prevDate);
          newTemporarySelectedDate.setHours(prevDate.getHours() - 12);
          return newTemporarySelectedDate;
        }

        if (selectedTimeRange === 'pm') {
          const newTemporarySelectedDate = new Date(prevDate);
          newTemporarySelectedDate.setHours(prevDate.getHours() + 12);
          return newTemporarySelectedDate;
        }
      }
    });
  };

  const canSelectAm = usesAmPm && (!isSameMinDay || (isSameMinDay && getTimeRange(minDate) !== 'pm'));
  const canSelectPm = usesAmPm && (!isSameMaxDay || (isSameMaxDay && getTimeRange(maxDate) !== 'am'));

  return (
    <div className={commonStyles.field}>
      {/* 🆕 Label */}
      {label && (
      <div className={commonStyles.fieldHeader}>
        {label && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className={commonStyles.label} htmlFor="date">
          {label}
          {required ? ' *' : null}
        </label>
        )}
        {InformationTooltip}
      </div>
      )}

      {/* 🕰️ Displayed dates */}
      <div
        ref={buttonRef}
        className={commonStyles.inputContainer}
        style={disabled ? inputContainerDisabled : undefined}
        role="button"
        tabIndex="0"
        onClick={() => {
          if (!disabled) {
            setOpenCalendar(true);
            if (!temporarySelectedDate && minDate) setTemporarySelectedDate(minDate);
          }
        }}
      >
        {/* Date */}
        <div style={{
          display: 'flex', alignItems: 'center', minWidth: dateOnly ? '100%' : '48%', maxWidth: dateOnly ? '100%' : '62%', overflow: 'hidden',
        }}
        >
          <span style={iconStyle}>
            <CalendarIcon stroke="var(--color-night-gray9" />
          </span>
          <span style={getTextStyle(!!formatDate(date, intl, dateFormatOptions))}>
            {formatDate(date, intl, dateFormatOptions) || intl.formatMessage({ id: placeholderId || 'placeholder.date' })}
          </span>
        </div>
        {!dateOnly && (
        <React.Fragment>
          {/* Divider */}
          <div style={verticalDividerStyle} />
          {/* Time */}
          <div style={{
            display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden',
          }}
          >
            <span style={iconStyle}>
              <ClockIcon stroke="var(--color-night-gray9" />
            </span>
            <span style={getTextStyle(!!formatDate(date, intl, { ...timeFormatOptions, hour12: usesAmPm }))}>
              {formatDate(date, intl, { ...timeFormatOptions, hour12: usesAmPm }) || intl.formatMessage({ id: 'placeholder.time' })}
            </span>
          </div>
          {/* Clear icon - only visible when date has a value */}
          {date && (
            <div
              style={crossIconStyle}
              onClick={(e) => {
                e.stopPropagation(); // Prevent opening the calendar
                onChange(null); // Clear the date value
              }}
            >
              <CrossIcon stroke="var(--color-night-gray9)" />
            </div>
          )}
        </React.Fragment>
        )}
      </div>

      {/* ❌ Error */}
      {errorText ? <div style={errorTextStyles}>{errorText}</div> : null}

      {/* 🆕 Hint */}
      {hint && (
        <span className={commonStyles.hint}>{hint}</span>
      )}

      {/* 🪟 Popover */}
      <Popover
        open={!disabled && openCalendar}
        onClose={() => {
          setOpenCalendar(false);
          // If we close the pop over without validating, we reinit the temporary date to the one passed by parent component
          setTemporarySelectedDate(date || minDate || new Date());
        }}
        anchorEl={buttonRef.current}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: isMobile ? 0 : POPOVER_HORIZONTAL_OFFSET,
        }}
        style={{ transform: 'translateX(30px)' }}
        PaperProps={{
          style: {
            boxShadow: '0px 15px 16px 0px rgba(0, 0, 0, 0.14)',
            border: 'var(--border)',
            borderRadius: '8px',
          },
        }}
        {...popoverProps}
      >
        {calendarLabelId && <h5 className="px-4 my-3 text-center">{intl.formatMessage({ id: calendarLabelId })}</h5>}
        <hr className="my-0" />
        {/* 🗓️ Calendar */}
        {/* ❗ We have to use moment adapter with Material UI Picker current version so each date passed must be a moment object (and conversely we "unmoment" the date in the onChange handler before giving it back to parent component) */}
        <div className="px-2">
          <Calendar
            date={moment(temporarySelectedDate || minDate)}
            onChange={handleDateChange}
            {...(minDate !== null && { minDate })}
            {...(maxDate !== null && { maxDate })}
          />

        </div>
        {!dateOnly && (
        <React.Fragment>
          <hr className="mb-3 mt-0" />
          {/* 🕰️ Time selectors */}
          <div className="d-flex justify-content-center align-items-center" style={{ fontSize: '20px', gap: '5px' }}>
            {/* Hours */}
            <select
              value={(temporarySelectedDate && temporarySelectedDate.getHours()) || ''}
              style={selectTimeStyles}
              onChange={handleSelectHour}
            >
              {OptionsHours}
            </select>
            <span>H</span>
            {/* Minutes */}
            <select
              value={(temporarySelectedDate && temporarySelectedDate.getMinutes()) || ''}
              style={selectTimeStyles}
              onChange={handleSelectMinute}
            >
              {OptionsMinutes}
            </select>
            {/* 🌞 AM / 🌙 PM */}
            {usesAmPm ? (
              <div className="px-2 d-flex flex-column" style={{ position: 'absolute', right: '15px' }}>
                <div style={{ ...overlayStyles, transform: `translate(-50%, ${timeRange === 'am' ? '0' : '100%'})` }} />
                <label
                  style={{
                    ...amPmStyles,
                    cursor: (timeRange === 'am' || !canSelectAm) ? 'default' : 'pointer',
                    color: timeRange === 'am' ? 'white' : 'black',
                    opacity: canSelectAm ? 1 : 0.5,
                  }}
                  htmlFor="am"
                >
                  <span>AM</span>
                  <input id="am" value="am" disabled={timeRange === 'am' || !canSelectAm} name="amPm" type="radio" className="d-none" checked={timeRange === 'am'} onChange={handleSelectTimeRange} />
                </label>
                <label
                  style={{
                    ...amPmStyles,
                    cursor: (timeRange === 'pm' || !canSelectPm) ? 'default' : 'pointer',
                    color: timeRange === 'pm' ? 'white' : 'black',
                    opacity: canSelectPm ? 1 : 0.5,
                  }}
                  htmlFor="pm"
                >
                  <span>PM</span>
                  <input id="pm" value="pm" disabled={timeRange === 'pm' || !canSelectPm} name="amPm" type="radio" className="d-none" checked={timeRange === 'pm'} onChange={handleSelectTimeRange} />
                </label>
              </div>
            ) : null}
          </div>
          <hr className="mb-0 mt-3" />
        </React.Fragment>
        )}
        {/* ✅ Validate button */}
        <div style={{
          display: 'flex', padding: 16, gap: 6, flexDirection: 'row-reverse',
        }}
        >
          <Button2
            type="primary"
            size="small"
            onClick={() => {
              setOpenCalendar(false);
              onChange(temporarySelectedDate);
            }}
          >
            <LocalizedMessage id="button.apply" />
          </Button2>
          <Button2
            type="secondaryOutline"
            size="small"
            onClick={() => {
              setOpenCalendar(false);
            }}
          >
            <LocalizedMessage id="button.cancel" />
          </Button2>
          <Button2
            type="secondaryOutline"
            size="small"
            iconOnly
            onClick={() => {
              onChange(null);
              setOpenCalendar(false);
            }}
          >
            <RefreshIcon stroke="var(--color-night-gray9)" />
          </Button2>
        </div>
      </Popover>
    </div>
  );
};

DateTimePicker3.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  calendarLabelId: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  popoverProps: PropTypes.object,
  placeholderId: PropTypes.string,
  tooltipId: PropTypes.string,
  required: PropTypes.bool,
  date: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  dateOnly: PropTypes.bool,
  onChange: PropTypes.func,
  intl: PropTypes.object.isRequired,
  errorText: PropTypes.string,
  hint: PropTypes.string,
};

DateTimePicker3.defaultProps = {
  type: null,
  disabled: false,
  calendarLabelId: null,
  label: null,
  tooltipId: null,
  required: false,
  popoverProps: null,
  placeholderId: null,
  onChange: null,
  date: null,
  dateOnly: false,
  minDate: null,
  maxDate: null,
  errorText: null,
  hint: null,
};

module.exports = compose(
  injectIntl,
)(DateTimePicker3);



// WEBPACK FOOTER //
// ./components/core/DateTimePicker3.jsx